import { useEffect, useMemo, useState } from 'react';
import { Alert, Autocomplete, Box, TextField, Typography } from '@mui/material';
import FrontRoyalSpinner from 'FrontRoyalSpinner';
import { useAngularContext } from 'AngularContext';
import { renderOptionKeyedById } from './renderOptionKeyedById';
import SelectedChipList from './SelectedChipList';
import preventBackspaceRemoval from './preventBackspaceRemoval';
import { type ContentItemGroup } from './ContentItemEditor.types';

type GroupModel = {
    cachedIndex: () => Promise<ContentItemGroup[]>;
};

type Props = {
    // The groups on the locale pack, which every translation of the course shares.
    value: ContentItemGroup[];
    onChange: (groups: ContentItemGroup[]) => void;
};

function sortByName<T extends { name: string }>(items: T[]) {
    return [...items].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
}

/*
    The groups picker inside the locale editor, replacing the Angular item-groups-editor for the
    course editor. The Angular directive stays where it is for the schedule admin and for the
    playlist and lesson editors.

    Group.cachedIndex() holds its result for the life of the page and nothing invalidates it, so a
    group created elsewhere in this session is missing until a reload. The Angular editor had the
    same limit.
*/
export default function LocalePackGroupsField({ value, onChange }: Props) {
    const $injector = useAngularContext();
    const [options, setOptions] = useState<ContentItemGroup[] | null>(null);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        let current = true;
        const Group = $injector.get<GroupModel>('Group');

        Group.cachedIndex()
            .then(groups => {
                if (current) setOptions(groups);
            })
            .catch(() => {
                if (current) setFailed(true);
            });

        return () => {
            current = false;
        };
    }, [$injector]);

    // Only the id and the name reach the server. Holding the whole record would make every save
    // look dirty.
    const sortedOptions = useMemo(() => sortByName((options || []).map(({ id, name }) => ({ id, name }))), [options]);
    const chips = useMemo(() => sortByName(value).map(group => ({ key: group.id, label: group.name })), [value]);

    if (failed) {
        return <Alert severity="error">Could not load the list of groups. Reload the page to try again.</Alert>;
    }

    if (!options) return <FrontRoyalSpinner className="inline" />;

    return (
        <div>
            <Typography variant="body2" sx={{ marginBottom: 1 }}>
                Groups are shared amongst all linked translations.
            </Typography>

            <Autocomplete
                multiple
                fullWidth
                filterSelectedOptions
                disableClearable
                value={value}
                options={sortedOptions}
                getOptionLabel={group => group.name}
                isOptionEqualToValue={(option, selected) => option.id === selected.id}
                renderValue={() => null}
                onChange={(_event, groups) => onChange(groups)}
                onKeyDown={preventBackspaceRemoval}
                renderOption={renderOptionKeyedById(group => group.name)}
                renderInput={props => <TextField {...props} label="Groups" />}
            />

            {/* The chips belong to the input above them, so they keep a gap from it. */}
            <Box sx={{ marginTop: 2 }}>
                <SelectedChipList
                    items={chips}
                    layout="wrap"
                    onRemove={groupId => onChange(value.filter(group => group.id !== groupId))}
                />
            </Box>
        </div>
    );
}
